import React, { useMemo } from "react";
import { addDays } from "date-fns";
import { Clock, Moon, Sun } from "lucide-react";
import useCalendarStore from "@/store/calendar-store";
import { Skeleton } from "@heroui/react";
import { useTranslations } from "../../../../../i18n";
import { dateToLocalTime, formatInTz } from "@/utils/format-date-with-locale";
import { TimeSlot } from "./timeslot";
import type { ClassSession } from "@/types/ClassSession";

const getEventStart = (event: any) => event.startAt ?? event.startTime;

interface WeeklyViewProps {
  classSessions: any[];
  onEventClick: (event: any) => void;
  customEventRender?: (event: any) => React.ReactNode;
  isLoading: boolean;
}

// Memoized DayColumn component
const DayColumn = ({
  date,
  timeOfDay,
  classSessions,
  isLoading,
  onEventClick,
  customEventRender,
  t,
  timezone,
}: {
  date: Date;
  timeOfDay: string;
  classSessions: ClassSession[];
  isLoading: boolean;
  onEventClick: (event: any) => void;
  customEventRender?: (event: any) => React.ReactNode;
  handleIconClick?: (drawer: string | null) => void;
  t: any; // Replace with your translation function
  timezone: string;
}) => {
  // Filter events for this day and time period
  const dayEvents = useMemo(() => {
    if (!classSessions || !classSessions.length) return [];

    return classSessions.filter((event) => {
      // Convert event time to organization timezone
      const eventStart = getEventStart(event);
      if (!eventStart) return false;
      const eventDate = dateToLocalTime(new Date(eventStart), timezone);
      const hour = eventDate.getHours();

      // Format both dates in the same timezone for comparison
      const isMatchingDate =
        formatInTz(eventDate, "yyyy-MM-dd", timezone) ===
        formatInTz(date, "yyyy-MM-dd", timezone);

      if (timeOfDay === "morning")
        return isMatchingDate && hour >= 6 && hour < 12;
      if (timeOfDay === "afternoon")
        return isMatchingDate && hour >= 12 && hour < 17;
      if (timeOfDay === "evening") return isMatchingDate && hour >= 17;

      return false;
    });
  }, [date, classSessions, timeOfDay, timezone]);

  if (isLoading) {
    return (
      <div className="flex-1 min-w-[100px] pt-2">
        <div key={timeOfDay} className="space-y-3">
          <Skeleton className="rounded-lg">
            <div className="h-24 rounded-lg bg-default-300" />
          </Skeleton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col gap-2 overflow-x-hidden overflow-y-hidden min-w-[100px] pt-2">
      {dayEvents.map((event) => (
        <TimeSlot
          key={event.id}
          event={event}
          onEventClick={onEventClick}
          customEventRender={customEventRender}
          t={t}
          timezone={timezone}
        />
      ))}
    </div>
  );
};

DayColumn.displayName = "DayColumn";

// Memoized TimeSection component
const TimeSection = ({
  title,
  icon: Icon,
  timeOfDay,
  classSessions,
  isLoading,
  onEventClick,
  customEventRender,
  startOfTheWeek,
  t,
  timezone,
}: {
  title: string;
  icon: React.ComponentType<any>;
  timeOfDay: string;
  classSessions: ClassSession[];
  isLoading: boolean;
  onEventClick: (event: any) => void;
  customEventRender?: (event: any) => React.ReactNode;
  startOfTheWeek: Date;
  t: any; // Replace with your translation function
  timezone: string;
}) => {
  const days = useMemo(() => {
    return [...Array(7)].map((_, index) => (
      <DayColumn
        key={index}
        date={addDays(startOfTheWeek, index)}
        timeOfDay={timeOfDay}
        classSessions={classSessions}
        isLoading={isLoading}
        onEventClick={onEventClick}
        customEventRender={customEventRender}
        t={t}
        timezone={timezone}
      />
    ));
  }, [
    classSessions,
    isLoading,
    onEventClick,
    startOfTheWeek,
    timeOfDay,
    customEventRender,
    t,
    timezone,
  ]);

  return (
    <div>
      <div className="flex items-center gap-2 text-gray-600 border-b border-gray-200 pb-1 mb-2">
        <Icon size={20} />
        <span className="font-medium">{title}</span>
      </div>
      <div className="flex gap-3 pb-3">{days}</div>
    </div>
  );
};

TimeSection.displayName = "TimeSection";

// Memoized WeekDays component
const WeekDays = ({
  startOfTheWeek,
  timezone,
}: {
  startOfTheWeek: Date;
  timezone: string;
}) => {
  const days = useMemo(() => {
    return [...Array(7)].map((_, index) => {
      const currentDate = addDays(startOfTheWeek, index);
      const today = new Date();

      // Format using timezone-aware functions
      const isToday =
        formatInTz(currentDate, "yyyy-MM-dd", timezone) ===
        formatInTz(today, "yyyy-MM-dd", timezone);

      return (
        <div className="flex-1 min-w-[100px]" key={index}>
          <div className="text-center mb-4">
            <div className="font-medium">
              {formatInTz(currentDate, "EEE", timezone)}
            </div>
            <div
              className={`inline-flex items-center justify-center w-8 h-8 text-xl rounded-full
                ${isToday ? "bg-primary text-white" : ""}`}
            >
              {formatInTz(currentDate, "d", timezone)}
            </div>
          </div>
        </div>
      );
    });
  }, [startOfTheWeek, timezone]);

  return <div className="flex gap-4 overflow-x-auto">{days}</div>;
};

WeekDays.displayName = "WeekDays";

const WeeklyView = ({
  classSessions,
  onEventClick,
  customEventRender,
  isLoading,
}: WeeklyViewProps) => {
  const t = useTranslations("calendar");
  const { startOfTheWeek } = useCalendarStore();

  const timezone = "Europe/Zurich"; //  organization?.timezone ||  TODO

  // Memoize whether there are events in each time period to avoid recalculation
  const { hasMorningEvents, hasAfternoonEvents, hasEveningEvents } =
    useMemo(() => {
      if (isLoading) {
        return {
          hasMorningEvents: true,
          hasAfternoonEvents: true,
          hasEveningEvents: true,
        };
      }

      const hasMorning = classSessions.some((event) => {
        const eventStart = getEventStart(event);
        if (!eventStart) return false;
        const eventDate = dateToLocalTime(new Date(eventStart), timezone);
        return eventDate.getHours() >= 6 && eventDate.getHours() < 12;
      });

      const hasAfternoon = classSessions.some((event) => {
        const eventStart = getEventStart(event);
        if (!eventStart) return false;
        const eventDate = dateToLocalTime(new Date(eventStart), timezone);
        return eventDate.getHours() >= 12 && eventDate.getHours() < 17;
      });

      const hasEvening = classSessions.some((event) => {
        const eventStart = getEventStart(event);
        if (!eventStart) return false;
        const eventDate = dateToLocalTime(new Date(eventStart), timezone);
        return eventDate.getHours() >= 17;
      });

      return {
        hasMorningEvents: hasMorning,
        hasAfternoonEvents: hasAfternoon,
        hasEveningEvents: hasEvening,
      };
    }, [classSessions, isLoading, timezone]);

  return (
    <div
      className="w-full max-w-7xl mx-auto py-4 overflow-y-hidden"
      id="week-view"
    >
      <WeekDays startOfTheWeek={startOfTheWeek} timezone={timezone} />

      <div className="flex flex-col gap-4 overflow-y-hidden">
        {hasMorningEvents && (
          <TimeSection
            title={t("morning")}
            icon={Clock}
            timeOfDay="morning"
            classSessions={classSessions}
            isLoading={isLoading}
            onEventClick={onEventClick}
            customEventRender={customEventRender}
            startOfTheWeek={startOfTheWeek}
            t={t}
            timezone={timezone}
          />
        )}

        {hasAfternoonEvents && (
          <TimeSection
            title={t("afternoon")}
            icon={Sun}
            timeOfDay="afternoon"
            classSessions={classSessions}
            isLoading={isLoading}
            onEventClick={onEventClick}
            customEventRender={customEventRender}
            startOfTheWeek={startOfTheWeek}
            t={t}
            timezone={timezone}
          />
        )}

        {hasEveningEvents && (
          <TimeSection
            title={t("evening")}
            icon={Moon}
            timeOfDay="evening"
            classSessions={classSessions}
            isLoading={isLoading}
            onEventClick={onEventClick}
            customEventRender={customEventRender}
            startOfTheWeek={startOfTheWeek}
            t={t}
            timezone={timezone}
          />
        )}
      </div>
    </div>
  );
};

export default WeeklyView;
